import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { FormattedMessage } from "react-intl";
import { ICON_TYPE } from "components/Icon";
import { colors, fontSize } from "style";
import { useAuth, useLinks } from "hooks";
import { MenuItemLogout, MenuItemSquads } from "components/Nav/SideNav/MenuItems";
import { SideNavLink, SideNavTooltip } from "components/Nav/SideNav";
import { PrimaryButton } from "components/Buttons";
import { SquadFormAsClient } from "components/Squads";

/**
 * MenuItemsByUserType
 */
const MenuItemsByUserType = () => {
  const { isPartner, isFreelancer, isRecruiter, isClient, isSignalClient, isShowcase, isAdmin, teams, ...me } =
    useAuth();
  const links = useLinks();
  const settingsLink = SETTINGS_LINKS(links, me)[me?.memberRole];

  const MenuItems = () => {
    if (isClient) {
      return <ClientMenu {...me} />;
    } else if (isFreelancer) {
      return <MemberMenu {...me} />;
    } else if (isSignalClient) {
      return <SignalClientMenu teams={teams} {...me} />;
    } else if (isShowcase) {
      return <ShowcaseMenu />;
    } else if (isPartner) {
      return <PartnerMenu />;
    } else if (isRecruiter) {
      return <RecruiterMenu />;
    } else if (isAdmin) {
      return <AdminMenu />;
    }
  };

  return (
    <>
      <MenuItems />
      <StyledLabel>
        <FormattedMessage id="Nav.SideNav.LabelAccount" />
      </StyledLabel>
      {settingsLink && (
        <SideNavLink type={ICON_TYPE.gear} to={settingsLink} routerEnabled end>
          <FormattedMessage id="Nav.SideNav.LabelSettings" />
        </SideNavLink>
      )}
      <MenuItemLogout isShowcase={isShowcase} />
    </>
  );
};

const SignalClientMenu = ({ teams, organization }) => {
  const [showForm, setShowForm] = useState(false);
  const links = useLinks();

  return (
    <>
      <SquadFormAsClient
        show={showForm}
        onClose={() => setShowForm(false)}
        onComplete={() => setShowForm(false)}
        organizationId={organization?.slug}
      />
      <StyledLabel>
        <FormattedMessage id={`Nav.SideNav.LabelSquadsSignal`} />
      </StyledLabel>
      {teams?.map((team) => (
        <MenuItemSquads key={team?.slug} team={team} />
      ))}
      {!teams || teams.length === 0 ? (
        <PrimaryButton variant="green" onClick={() => setShowForm(true)}>
          <SideNavTooltip textId="Nav.SideNav.AddTeamSignal" values={{ icon: "+" }} />
        </PrimaryButton>
      ) : (
        <SideNavLink type={ICON_TYPE.plus} onClick={() => setShowForm(true)}>
          <SideNavTooltip textId="Nav.SideNav.AddTeamSignal" values={{ icon: "" }} />
        </SideNavLink>
      )}
      <StyledLabel>
        <FormattedMessage id="Nav.SideNav.LabelOtherSignal" />
      </StyledLabel>
      <SideNavLink type={ICON_TYPE.organization} to={links.organization_profile(organization?.slug)} routerEnabled end>
        <FormattedMessage id="Nav.SideNav.OrganizationSignal" />
      </SideNavLink>
      <StyledLabel>
        <FormattedMessage id="Nav.SideNav.LabelTalent" />
      </StyledLabel>
      <SideNavLink type={ICON_TYPE.search} to={links.client_search} routerEnabled end>
        <SideNavTooltip textId="Nav.SideNav.LabelTalentSearch" />
      </SideNavLink>
    </>
  );
};

const ClientMenu = ({ organization, isPrimaryContact, isTalentSimpleClient }) => {
  const links = useLinks();

  return (
    <>
      <StyledLabel>
        <FormattedMessage id="Nav.SideNav.LabelTalent" />
      </StyledLabel>
      <SideNavLink type={ICON_TYPE.calendar} to={links.client_mission_board} routerEnabled>
        <SideNavTooltip textId="Nav.SideNav.LabelBoard" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.user} to={links.client_applications} routerEnabled end>
        <SideNavTooltip textId="Nav.SideNav.LabelTalentApplications" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.search} to={links.client_search} routerEnabled end>
        <SideNavTooltip textId="Nav.SideNav.LabelTalentSearch" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.heart} to={links.favorites} routerEnabled>
        <SideNavTooltip textId="Nav.SideNav.Client.LabelFavorites" />
      </SideNavLink>
      <SquadsMenuItem />
      {isPrimaryContact && (
        <>
          <StyledLabel>
            <FormattedMessage id="Nav.SideNav.LabelOrganization" />
          </StyledLabel>
          <SideNavLink
            disabled={!isPrimaryContact}
            type={ICON_TYPE.organization}
            to={isPrimaryContact ? links.organization_profile(organization?.slug) : undefined}
            routerEnabled={isPrimaryContact}
          >
            <SideNavTooltip textId="Nav.SideNav.LabelOrganizationSettings" />
          </SideNavLink>
        </>
      )}
      {!isTalentSimpleClient && (
        <>
          <StyledLabel>
            <FormattedMessage id="Nav.SideNav.LabelCommunity" />
          </StyledLabel>
          <SideNavLink type={ICON_TYPE.townHall} to={links.community} routerEnabled end>
            <FormattedMessage id="Nav.SideNav.LabelCommunityHub" />
          </SideNavLink>
        </>
      )}
    </>
  );
};

const MemberMenu = ({ profileSlug, canViewEditRate }) => {
  const links = useLinks();

  return (
    <>
      <SideNavLink type={ICON_TYPE.home} to={links.home} routerEnabled>
        <FormattedMessage id="Nav.SideNav.LabelHome" />
      </SideNavLink>
      <StyledLabel>
        <FormattedMessage id="Nav.SideNav.LabelMissions" />
      </StyledLabel>
      <SideNavLink type={ICON_TYPE.calendar} to={links.freelancer_mission_board} routerEnabled>
        <FormattedMessage id="Nav.SideNav.LabelBoard" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.checkDotted} to={links.mission_applications} routerEnabled>
        <FormattedMessage id="Nav.SideNav.LabelApplications" />
      </SideNavLink>
      <SquadsMenuItem />
      <StyledLabel>
        <FormattedMessage id="Nav.SideNav.LabelMember" />
      </StyledLabel>
      <SideNavLink type={ICON_TYPE.user} to={links.private_profile(profileSlug)} routerEnabled end>
        <FormattedMessage id="Nav.SideNav.LabelProfile" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.hourglass} to={links.time_sheet} routerEnabled>
        <FormattedMessage id="Nav.SideNav.LabelTimeSheet" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.coin} to={links.rate(profileSlug)} routerEnabled end>
        <FormattedMessage id={canViewEditRate ? "Nav.SideNav.LabelRate" : "Nav.SideNav.LabelAvailability"} />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.video} to={links.profile_video_interviews(profileSlug)} routerEnabled>
        <FormattedMessage id="Nav.SideNav.LabelVideoInterviews" />
      </SideNavLink>
      <StyledLabel>
        <FormattedMessage id="Nav.SideNav.LabelCommunity" />
      </StyledLabel>
      <SideNavLink type={ICON_TYPE.townHall} to={links.community} routerEnabled end>
        <FormattedMessage id="Nav.SideNav.LabelCommunityHub" />
      </SideNavLink>
    </>
  );
};

const ShowcaseMenu = () => {
  const links = useLinks();

  return (
    <>
      <StyledLabel>
        <FormattedMessage id="Nav.SideNav.LabelTalent" />
      </StyledLabel>
      <SideNavLink type={ICON_TYPE.search} to={links.showcase_search} routerEnabled end>
        <SideNavTooltip textId="Nav.SideNav.LabelTalentSearch" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.mentorship} to={links.showcase_team_builder} routerEnabled end>
        <SideNavTooltip textId="Nav.SideNav.LabelTalentTeamBuilder" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.star} to={links.featured_teams} routerEnabled>
        <SideNavTooltip textId="Nav.SideNav.LabelFeaturedTeams" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.heart} to={links.favorites} routerEnabled>
        <SideNavTooltip textId="Nav.SideNav.Client.LabelFavorites" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.user} to={links.client_applications} routerEnabled end>
        <SideNavTooltip textId="Nav.SideNav.LabelTalentApplications" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.calendar} to={links.client_mission_board} routerEnabled>
        <SideNavTooltip textId="Nav.SideNav.LabelBoard" />
      </SideNavLink>
      <SquadsMenuItem />
      <StyledLabel>
        <FormattedMessage id="Nav.SideNav.LabelCommunity" />
      </StyledLabel>
      <SideNavLink type={ICON_TYPE.townHall} to={links.community} routerEnabled end>
        <FormattedMessage id="Nav.SideNav.LabelCommunityHub" />
      </SideNavLink>
    </>
  );
};

const PartnerMenu = () => {
  const links = useLinks();

  return (
    <>
      <SideNavLink type={ICON_TYPE.network} to={links.network} routerEnabled>
        <FormattedMessage id="Nav.SideNav.LabelNetwork" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.mentorship} to={links.partner_mission_board} routerEnabled>
        <FormattedMessage id="Nav.SideNav.LabelMissions" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.clock} to={links.timecards} routerEnabled end>
        <FormattedMessage id="Nav.SideNav.LabelTimeReport" />
      </SideNavLink>
    </>
  );
};

const RecruiterMenu = () => {
  const links = useLinks();

  return (
    <>
      <SideNavLink type={ICON_TYPE.network} to={links.network} routerEnabled end>
        <FormattedMessage id="Nav.SideNav.LabelNetwork" />
      </SideNavLink>
      <SideNavLink type={ICON_TYPE.mentorship} to={links.recruiter_mission_board} routerEnabled>
        <FormattedMessage id="Nav.SideNav.LabelMissions" />
      </SideNavLink>
    </>
  );
};

const AdminMenu = () => {
  const links = useLinks();

  return ADMIN_MENU(links).map((item, index) => (
    <SideNavLink key={index} type={item.icon} to={item.to} routerEnabled>
      <FormattedMessage id={item.textId} />
    </SideNavLink>
  ));
};

const SquadsMenuItem = () => {
  const { teams, isClient, isShowcase } = useAuth();
  const hasSquads = teams?.length > 0;

  if (!hasSquads) return null;

  return (
    <>
      <StyledLabel>
        <FormattedMessage id={`Nav.SideNav.Label${isShowcase || isClient ? "Teams" : "Squads"}`} />
      </StyledLabel>
      {teams?.map((team, index) => (
        <MenuItemSquads key={index} team={team} />
      ))}
    </>
  );
};

const StyledLabel = styled.label`
  text-transform: uppercase;
  font-size: ${fontSize.xxxsmall};
  letter-spacing: 0.2rem;
  font-weight: 600;
  color: ${colors.grayAnatomyLight1};
  margin-top: 2rem;
  margin-bottom: 0.5rem;

  @media (max-height: 850px) {
    margin-bottom: 0.25rem;
    margin-top: 1.5rem;
  }
`;

const ADMIN_MENU = (links) => [
  { icon: ICON_TYPE.performance, to: links.dashboard_home, textId: "Nav.SideNav.LabelDashboard" },
  { icon: ICON_TYPE.network, to: links.network, textId: "Nav.SideNav.LabelNetwork" },
  { icon: ICON_TYPE.board, to: links.missions, textId: "Nav.SideNav.LabelMissions" },
  { icon: ICON_TYPE.mentorship, to: links.squads, textId: "Nav.SideNav.AdminSquads" },
  { icon: ICON_TYPE.organization, to: links.organizations, textId: "Nav.SideNav.LabelOrganizations" },
  { icon: ICON_TYPE.listBullet, to: links.lists, textId: "Nav.SideNav.LabelLists" },
  { icon: ICON_TYPE.businessCard, to: links.partners, textId: "Nav.SideNav.LabelPartners" },
  { icon: ICON_TYPE.clock, to: links.timecards, textId: "Nav.SideNav.LabelTimeReport" },
  { icon: ICON_TYPE.gear, to: links.platform_settings, textId: "Nav.SideNav.LabelPlatformSettings" },
];

const SETTINGS_LINKS = (links, me) => ({
  member: links.settings(me?.profileSlug),
  client: links.user_settings,
});

SignalClientMenu.propTypes = {
  teams: PropTypes.array,
  organization: PropTypes.object,
};

MemberMenu.propTypes = {
  profileSlug: PropTypes.string.isRequired,
  canViewEditRate: PropTypes.bool.isRequired,
};

ClientMenu.propTypes = {
  organization: PropTypes.object,
  isPrimaryContact: PropTypes.bool,
  isTalentSimpleClient: PropTypes.bool,
};

export { ADMIN_MENU };

export default MenuItemsByUserType;
